import React from "react";
import { Helmet } from "react-helmet";
import {
  FiArrowRight,
  FiCheck,
  FiBriefcase,
  FiUsers,
  FiFileText,
  FiBarChart2,
  FiClock,
  FiDatabase,
  FiZap,
  FiActivity,
  FiLayers,
  FiMessageSquare,
  FiTarget,
  FiSettings,
   FiSearch,
} from "react-icons/fi";

import Header from "../../../components/Header";
import Footer from "../../../components/Footer";

import "./ProfessionalServices.css";

const ProfessionalServices = () => {
  return (
    <>
      <Helmet>
        <title>AI Solutions for Professional Services | Dropgear IO</title>

        <meta
          name="description"
          content="Dropgear IO helps professional service businesses automate workflows, organize knowledge, improve client operations, and build intelligent AI-powered systems."
        />
      </Helmet>

      <Header />

      <main className="ps-page">

        {/* =====================================================
            HERO
        ===================================================== */}
        <section className="ps-hero">
          <div className="ps-container ps-hero-grid">

            <div className="ps-hero-content">

              <span className="ps-eyebrow">
                AI FOR PROFESSIONAL SERVICES
              </span>

              <h1>
                Turn expertise into
                <span> intelligent operations.</span>
              </h1>

              <p>
                Dropgear IO helps professional service businesses automate
                repetitive work, organize business knowledge, coordinate
                client workflows, and give teams intelligent tools to move
                projects forward.
              </p>

              <div className="ps-hero-actions">

                <a href="/book-demo" className="ps-btn-primary">
                  Book a Demo
                  <FiArrowRight />
                </a>

                <a
                  href="#professional-use-cases"
                  className="ps-btn-secondary"
                >
                  Explore Solutions
                </a>

              </div>

              <div className="ps-hero-points">

                <span>
                  <FiCheck />
                  Workflow automation
                </span>

                <span>
                  <FiCheck />
                  Knowledge intelligence
                </span>

                <span>
                  <FiCheck />
                  Client operations
                </span>

              </div>

            </div>


            {/* DASHBOARD */}
            <div className="ps-dashboard-wrapper">

              <div className="ps-dashboard">

                <div className="ps-dashboard-top">

                  <div>
                    <span>Professional Services</span>
                    <strong>Operations Workspace</strong>
                  </div>

                  <div className="ps-live">
                    <i></i>
                    Live
                  </div>

                </div>


                <div className="ps-dashboard-summary">

                  <div className="ps-summary-card">

                    <div className="ps-summary-icon">
                      <FiBriefcase />
                    </div>

                    <span>Active Projects</span>

                    <strong>42</strong>

                    <small>
                      <FiActivity />
                      8 updated today
                    </small>

                  </div>


                  <div className="ps-summary-card">

                    <div className="ps-summary-icon">
                      <FiClock />
                    </div>

                    <span>Workflow Time</span>

                    <strong>18.6h</strong>

                    <small>
                      <FiZap />
                      Automated
                    </small>

                  </div>

                </div>


                <div className="ps-project-card">

                  <div className="ps-project-header">

                    <div>
                      <span>Project Operations</span>
                      <strong>Current Activity</strong>
                    </div>

                    <FiBarChart2 />

                  </div>


                  <div className="ps-project-progress">

                    <div>
                      <span>Client onboarding</span>
                      <b>88%</b>
                    </div>

                    <div className="ps-progress-track">
                      <span style={{ width: "88%" }}></span>
                    </div>

                  </div>


                  <div className="ps-project-progress">

                    <div>
                      <span>Project delivery</span>
                      <b>72%</b>
                    </div>

                    <div className="ps-progress-track">
                      <span style={{ width: "72%" }}></span>
                    </div>

                  </div>


                  <div className="ps-project-progress">

                    <div>
                      <span>Knowledge processing</span>
                      <b>94%</b>
                    </div>

                    <div className="ps-progress-track">
                      <span style={{ width: "94%" }}></span>
                    </div>

                  </div>

                </div>


                <div className="ps-dashboard-tasks">

                  <div className="ps-task">

                    <span>
                      <FiFileText />
                    </span>

                    <div>
                      <strong>Proposal analysis</strong>
                      <small>AI workflow completed</small>
                    </div>

                    <b>Done</b>

                  </div>


                  <div className="ps-task">

                    <span>
                      <FiMessageSquare />
                    </span>

                    <div>
                      <strong>Client request</strong>
                      <small>Needs team review</small>
                    </div>

                    <b>Review</b>

                  </div>


                  <div className="ps-task">

                    <span>
                      <FiDatabase />
                    </span>

                    <div>
                      <strong>Knowledge sync</strong>
                      <small>Processing information</small>
                    </div>

                    <b>Active</b>

                  </div>

                </div>

              </div>

            </div>

          </div>
        </section>


        {/* =====================================================
            INTRO
        ===================================================== */}
        <section className="ps-intro">

          <div className="ps-container">

            <div className="ps-section-heading">

              <span className="ps-eyebrow">
                THE PROFESSIONAL SERVICES CHALLENGE
              </span>

              <h2>
                Your expertise should create value,
                <span> not administrative overhead.</span>
              </h2>

              <p>
                Professional service businesses depend on people, expertise,
                relationships, documents, and repeatable processes. As teams
                grow, managing those moving parts manually can consume valuable
                time. Dropgear IO helps turn those operational processes into
                intelligent workflows.
              </p>

            </div>

          </div>

        </section>


        {/* =====================================================
            USE CASES
        ===================================================== */}
        <section
          className="ps-use-cases"
          id="professional-use-cases"
        >

          <div className="ps-container">

            <div className="ps-section-heading centered">

              <span className="ps-eyebrow">
                AI CAPABILITIES
              </span>

              <h2>
                AI built around
                <span> service delivery.</span>
              </h2>

              <p>
                Apply intelligent automation across the workflows that keep
                professional service teams moving.
              </p>

            </div>


            <div className="ps-use-grid">

              {/* CARD 1 */}
              <div className="ps-use-card">

                <div className="ps-card-icon">
                  <FiUsers />
                </div>

                <h3>Client Operations</h3>

                <p>
                  Organize client requests, communication, documents, and
                  recurring activities into structured workflows.
                </p>

                <ul>
                  <li>
                    <FiCheck />
                    Client intake
                  </li>

                  <li>
                    <FiCheck />
                    Request routing
                  </li>

                  <li>
                    <FiCheck />
                    Follow-up workflows
                  </li>
                </ul>

              </div>


              {/* CARD 2 */}
              <div className="ps-use-card">

                <div className="ps-card-icon">
                  <FiFileText />
                </div>

                <h3>Document Intelligence</h3>

                <p>
                  Extract useful information from proposals, contracts,
                  reports, briefs, and other business documents.
                </p>

                <ul>
                  <li>
                    <FiCheck />
                    Document extraction
                  </li>

                  <li>
                    <FiCheck />
                    Classification
                  </li>

                  <li>
                    <FiCheck />
                    Information retrieval
                  </li>
                </ul>

              </div>


              {/* CARD 3 */}
              <div className="ps-use-card">

                <div className="ps-card-icon">
                  <FiBriefcase />
                </div>

                <h3>Project Operations</h3>

                <p>
                  Coordinate project activities, tasks, deadlines, and
                  operational updates across teams.
                </p>

                <ul>
                  <li>
                    <FiCheck />
                    Task coordination
                  </li>

                  <li>
                    <FiCheck />
                    Project monitoring
                  </li>

                  <li>
                    <FiCheck />
                    Workflow alerts
                  </li>
                </ul>

              </div>


              {/* CARD 4 */}
              <div className="ps-use-card">

                <div className="ps-card-icon">
                  <FiMessageSquare />
                </div>

                <h3>AI Assistants</h3>

                <p>
                  Give teams intelligent assistants that can find information,
                  summarize work, and support everyday operational tasks.
                </p>

                <ul>
                  <li>
                    <FiCheck />
                    Knowledge queries
                  </li>

                  <li>
                    <FiCheck />
                    Information summaries
                  </li>

                  <li>
                    <FiCheck />
                    Team assistance
                  </li>
                </ul>

              </div>


              {/* CARD 5 */}
              <div className="ps-use-card">

                <div className="ps-card-icon">
                  <FiTarget />
                </div>

                <h3>Sales & Proposals</h3>

                <p>
                  Support proposal preparation, lead workflows, opportunity
                  research, and pre-sales operations.
                </p>

                <ul>
                  <li>
                    <FiCheck />
                    Lead qualification
                  </li>

                  <li>
                    <FiCheck />
                    Proposal assistance
                  </li>

                  <li>
                    <FiCheck />
                    Opportunity workflows
                  </li>
                </ul>

              </div>


              {/* CARD 6 */}
              <div className="ps-use-card">

                <div className="ps-card-icon">
                  <FiBarChart2 />
                </div>

                <h3>Business Intelligence</h3>

                <p>
                  Transform operational activity into structured insights that
                  help leaders understand workload, delivery, and performance.
                </p>

                <ul>
                  <li>
                    <FiCheck />
                    Operational dashboards
                  </li>

                  <li>
                    <FiCheck />
                    Performance summaries
                  </li>

                  <li>
                    <FiCheck />
                    Decision support
                  </li>
                </ul>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            PLATFORM
        ===================================================== */}
        <section className="ps-platform">

          <div className="ps-container ps-platform-grid">

            <div className="ps-platform-visual">

              <div className="ps-platform-window">

                <div className="ps-window-header">

                  <span>
                    Dropgear Professional Services
                  </span>

                  <div>
                    <i></i>
                    <i></i>
                    <i></i>
                  </div>

                </div>


                <div className="ps-window-body">

                  <div className="ps-workspace-heading">

                    <span>Team Workspace</span>

                    <strong>
                      Delivery Overview
                    </strong>

                  </div>


                  <div className="ps-workspace-metrics">

                    <div>
                      <span>Projects</span>
                      <strong>24</strong>
                    </div>

                    <div>
                      <span>Open Tasks</span>
                      <strong>86</strong>
                    </div>

                    <div>
                      <span>Client Requests</span>
                      <strong>17</strong>
                    </div>

                  </div>


                  <div className="ps-workflow-list">

                    <div>

                      <span>
                        <FiCheck />
                      </span>

                      <p>
                        Client onboarding
                        <small>Workflow completed</small>
                      </p>

                      <b>Done</b>

                    </div>


                    <div>

                      <span>
                        <FiClock />
                      </span>

                      <p>
                        Project review
                        <small>Waiting for approval</small>
                      </p>

                      <b>Review</b>

                    </div>


                    <div>

                      <span>
                        <FiZap />
                      </span>

                      <p>
                        Proposal preparation
                        <small>AI assistance active</small>
                      </p>

                      <b>Active</b>

                    </div>


                    <div>

                      <span>
                        <FiDatabase />
                      </span>

                      <p>
                        Knowledge update
                        <small>Information synchronized</small>
                      </p>

                      <b>Sync</b>

                    </div>

                  </div>

                </div>

              </div>

            </div>


            <div className="ps-platform-content">

              <span className="ps-eyebrow">
                ONE INTELLIGENT WORKSPACE
              </span>

              <h2>
                Bring your people,
                <span> processes, and knowledge together.</span>
              </h2>

              <p>
                Dropgear IO can connect operational workflows with the
                information teams use every day, helping professional service
                businesses reduce repetitive work while keeping their existing
                processes at the center.
              </p>


              <div className="ps-platform-list">

                <div>

                  <span>
                    <FiZap />
                  </span>

                  <div>

                    <h3>
                      Automate repetitive operations
                    </h3>

                    <p>
                      Reduce administrative tasks that consume valuable team
                      time.
                    </p>

                  </div>

                </div>


                <div>

                  <span>
                    <FiDatabase />
                  </span>

                  <div>

                    <h3>
                      Make knowledge easier to access
                    </h3>

                    <p>
                      Help teams find and understand important information
                      faster.
                    </p>

                  </div>

                </div>


                <div>

                  <span>
                    <FiUsers />
                  </span>

                  <div>

                    <h3>
                      Keep teams involved
                    </h3>

                    <p>
                      Route decisions, approvals, and exceptions to the right
                      people.
                    </p>

                  </div>

                </div>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            HOW IT WORKS
        ===================================================== */}
        <section className="ps-how">

          <div className="ps-container">

            <div className="ps-section-heading centered">

              <span className="ps-eyebrow">
                HOW IT WORKS
              </span>

              <h2>
                From scattered tasks to
                <span> connected workflows.</span>
              </h2>

              <p>
                Start with one operational process and gradually build an
                intelligent system around the way your organization works.
              </p>

            </div>


            <div className="ps-steps">

              <div className="ps-step">

                <div className="ps-step-number">
                  01
                </div>

                <div className="ps-step-icon">
                  <FiSearch />
                </div>

                <h3>
                  Understand
                </h3>

                <p>
                  Identify repetitive processes, information bottlenecks, and
                  areas where teams spend unnecessary time.
                </p>

              </div>


              <div className="ps-step">

                <div className="ps-step-number">
                  02
                </div>

                <div className="ps-step-icon">
                  <FiSettings />
                </div>

                <h3>
                  Design
                </h3>

                <p>
                  Turn the selected process into a structured workflow that AI
                  can assist or automate.
                </p>

              </div>


              <div className="ps-step">

                <div className="ps-step-number">
                  03
                </div>

                <div className="ps-step-icon">
                  <FiZap />
                </div>

                <h3>
                  Automate
                </h3>

                <p>
                  Connect the workflow to the relevant tools, information, and
                  business actions.
                </p>

              </div>


              <div className="ps-step">

                <div className="ps-step-number">
                  04
                </div>

                <div className="ps-step-icon">
                  <FiActivity />
                </div>

                <h3>
                  Improve
                </h3>

                <p>
                  Monitor results, collect feedback, and continuously improve
                  the workflow as your business evolves.
                </p>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            BENEFITS
        ===================================================== */}
        <section className="ps-benefits">

          <div className="ps-container ps-benefits-grid">

            <div className="ps-benefits-content">

              <span className="ps-eyebrow">
                BUILT FOR KNOWLEDGE-DRIVEN TEAMS
              </span>

              <h2>
                Give your team more time
                <span> for meaningful work.</span>
              </h2>

              <p>
                Professional services are built around expertise. Dropgear IO
                helps remove unnecessary operational friction so teams can
                spend more time serving clients, solving problems, and
                delivering outcomes.
              </p>


              <div className="ps-benefit-list">

                <div>
                  <FiCheck />
                  <span>Reduce administrative workload</span>
                </div>

                <div>
                  <FiCheck />
                  <span>Improve client response workflows</span>
                </div>

                <div>
                  <FiCheck />
                  <span>Make internal knowledge easier to access</span>
                </div>

                <div>
                  <FiCheck />
                  <span>Improve project visibility</span>
                </div>

                <div>
                  <FiCheck />
                  <span>Support teams with intelligent assistants</span>
                </div>

              </div>

            </div>


            <div className="ps-benefit-card">

              <div className="ps-benefit-card-top">

                <span>
                  <FiBarChart2 />
                </span>

                <div>

                  <small>
                    Operations Intelligence
                  </small>

                  <strong>
                    Services Overview
                  </strong>

                </div>

              </div>


              <div className="ps-metric">

                <div>

                  <span>
                    Workflow automation
                  </span>

                  <strong>
                    78%
                  </strong>

                </div>

                <div className="ps-metric-bar">
                  <span style={{ width: "78%" }}></span>
                </div>

              </div>


              <div className="ps-metric">

                <div>

                  <span>
                    Project visibility
                  </span>

                  <strong>
                    91%
                  </strong>

                </div>

                <div className="ps-metric-bar">
                  <span style={{ width: "91%" }}></span>
                </div>

              </div>


              <div className="ps-metric">

                <div>

                  <span>
                    Knowledge accessibility
                  </span>

                  <strong>
                    86%
                  </strong>

                </div>

                <div className="ps-metric-bar">
                  <span style={{ width: "86%" }}></span>
                </div>

              </div>


              <div className="ps-card-note">

                <FiLayers />

                Designed to evolve around the way your team already works.

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            RESPONSIBLE AI
        ===================================================== */}
        <section className="ps-responsible">

          <div className="ps-container ps-responsible-box">

            <div className="ps-responsible-icon">
              <FiUsers />
            </div>


            <div className="ps-responsible-content">

              <span className="ps-eyebrow">
                HUMAN + AI
              </span>

              <h2>
                AI should support expertise,
                <span> not replace it.</span>
              </h2>

              <p>
                Professional services depend on judgment and experience.
                Dropgear IO is designed to automate repeatable operational
                work while allowing people to stay involved in decisions that
                require context, expertise, and accountability.
              </p>


              <div className="ps-responsible-points">

                <span>
                  <FiCheck />
                  Human review where needed
                </span>

                <span>
                  <FiCheck />
                  Controlled workflow actions
                </span>

                <span>
                  <FiCheck />
                  Clear operational visibility
                </span>

                <span>
                  <FiCheck />
                  Flexible automation levels
                </span>

              </div>

            </div>

          </div>

        </section>


        {/* =====================================================
            CTA
        ===================================================== */}
        <section className="ps-cta">

          <div className="ps-container">

            <div className="ps-cta-box">

              <div>

                <span className="ps-eyebrow">
                  READY TO BUILD SMARTER?
                </span>

                <h2>
                  Turn your service operations
                  <span> into an intelligent advantage.</span>
                </h2>

                <p>
                  Explore how Dropgear IO can help automate repetitive work,
                  connect business knowledge, and give your team better tools
                  for delivering exceptional client outcomes.
                </p>

              </div>


              <a href="/contact" className="ps-btn-primary">

                Book a Demo

                <FiArrowRight />

              </a>

            </div>

          </div>

        </section>

      </main>

      <Footer />
    </>
  );
};

export default ProfessionalServices;